import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { SortType } from "@enums/SortType";
import { UserStatus } from "@enums/UserType";
import { IUser } from "@interfaces/IUser";
import { GroupService } from "@services/group.service.ajs";
import { ILoginHistory, LoginHistoryService } from "@services/loginHistory.service.ajs";
import { TranslationService } from "@services/translation.service";
import { UsersService } from "@services/users.service";
import * as angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import "./users.scss";

class UsersComponentController implements angular.IComponentController {

    // raw data
    userLoginHistories: ILoginHistory[];
    users: IUser[];

    selectedUser: IUser;

    tableHeaders: IVeriTableHeader[] = [];
    tableRows: IVeriTableRow<IUser>[] = [];

    // spinners
    loading = false;
    loadingLoginHistories = true;

    userFilter = "";
    showInactiveUsers = false;

    showSSOConfiguration = false;

    static $inject = ["$q", "$scope", "$timeout", "$mdDialog", "usersService", "veriTableService", "loginHistoryService", "translationService", "groupService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $scope: angular.IScope,
        private readonly $timeout: angular.ITimeoutService,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly usersService: UsersService,
        private readonly veriTableService: VeriTableService,
        private readonly loginHistoryService: LoginHistoryService,
        private readonly translationService: TranslationService,
        private readonly groupService: GroupService
    ) {
    }

    $onInit(): void {
        this.createTableHeaders();

        this.getData();
    }

    onUserCreated(user: IUser): void {
        this.showUserCreatedDialog().finally(() => {
            this.reloadData();
        });
    }

    onUserSaved(user: IUser): void {
        this.reloadData();
    }

    onUserDeactivated(user: IUser): void {
        this.reloadData();
    }

    onUserReactivated(user: IUser): void {
        this.reloadData();
    }

    onUserEditorCancelled(): void {
        this.clearSelectedUser();
    }

    addUser() {
        this.selectUser(this.usersService.createDefaultUserModel());
        this.$scope.$applyAsync();
    }

    selectUser(user: IUser) {
        this.selectedUser = copy(user);
    }

    clearSelectedUser() {
        this.selectedUser = null;
    }

    showHideInactiveUsers() {
        this.createTableRows();
    }

    showHideSSOConfiguration(show = true) {
        this.showSSOConfiguration = show;
        this.$scope.$applyAsync();
    }

    // ----------------------------------------------

    private reloadData(): void {
        this.loading = true;

        this.clearSelectedUser();
        this.$timeout(() => this.getData(true), 1000);
    }

    private showUserCreatedDialog() {
        const passwordResultDialog = this.$mdDialog
            .alert()
            .title(this.translationService.t('users.userCreated'))
            .htmlContent(this.translationService.t('users.accountCreated', [this.selectedUser.CurrentAccountUserMapping.Name, this.selectedUser.Email]))
            .ok(this.translationService.t('Common.OK'));

        return this.$mdDialog.show(passwordResultDialog)
    }

    private createTableHeaders() {
        this.tableHeaders = [
            {
                title: this.translationService.t('users.realName'),
                sortable: true,
                sortType: SortType.String
            },
            {
                title: this.translationService.t('Common.Email'),
                sortable: true,
                sortType: SortType.String
            },
            {
                title: this.translationService.t('users.groupPermissions'),
                sortable: false,
                excludeFromExport: true
            },
            {
                title: this.translationService.t('Common.Status'),
                sortable: true,
                width: 80
            },
            this.veriTableService.createDateTimeHeader(this.translationService.t('users.lastLogin'))
        ];
    }

    private createTableRows() {
        this.tableRows = [];

        this.users.forEach(user => {
            if (user.CurrentAccountUserMapping.Status == UserStatus.Inactive && !this.showInactiveUsers) {
                return;
            }

            var row: IVeriTableRow<IUser> = {
                data: user,
                cells: this.createTableRowCells(user),
                rowClassName: user.CurrentAccountUserMapping.Status == UserStatus.Active ? "" : "inactive-user"
            };

            this.tableRows.push(row);
        });

        this.$scope.$applyAsync();
    }

    private createTableRowCells(user: IUser): IVeriTableRowCell[] {
        var lastRecordCell
        var lastLoginRecord = this.userLoginHistories?.find(h => h.UserId == user.Id);

        if (!lastLoginRecord && this.loadingLoginHistories) {
            lastRecordCell = this.veriTableService.createNACell(null, this.translationService.t('Common.Updating'));
        } else {
            lastRecordCell = this.veriTableService.createDateTimeCell(lastLoginRecord ? lastLoginRecord.CreatedTime : null);
        }
        var cells: IVeriTableRowCell[] = [
            {
                contents: user.CurrentAccountUserMapping.Name,
                className: "bold"
            },
            {
                contents: user.Email
            },
            {
                contents: `<resource-group-display [user]="cell.data" [max]="1"></resource-group-display>`, data: user, excludeFromExport: true
            },
            {
                contents: UserStatus[user.CurrentAccountUserMapping.Status]
            },
            lastRecordCell
        ];

        return cells;
    }

    private getData(invalidateCache = false) {
        this.loading = true;

        const usersPromise = this.fetchUsers(invalidateCache);
        const loginHistoryPromise = this.loadLoginHistory();

        return this.$q
            .all([usersPromise, loginHistoryPromise])
            .then(() => {
                this.createTableRows();
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private loadLoginHistory(): angular.IPromise<void> {
        this.loadingLoginHistories = true;

        return this.loginHistoryService.getLatestLoginHistories().then(loginHistory => {
            this.userLoginHistories = loginHistory;
            this.loadingLoginHistories = false;
        });
    }

    private fetchUsers(invalidateCache = false): angular.IPromise<void> {
        if (invalidateCache) {
            this.users = [];
        }

        return this.usersService.getUsers({ includeAdmins: false, includeMappings: true }, invalidateCache).then(users => {
            this.users = users;
        })
    }
}

export class UsersComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            onUsersChanged: "&"
        };

        this.controller = UsersComponentController;
        this.template = require(`./users.html`);
    }
}
